import React, { Suspense, useEffect, useState } from "react";
import { Route, Switch, withRouter, Redirect } from "react-router-dom";
import classnames from "classnames";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";

import useStyles from "./styles";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { useLayoutState } from "../../context/LayoutContext";
import { usePageTracking } from "./../../usePageTracking";
import { getDevicesHealth } from "../../app/devicesSlice";
import Error from "../../pages/error/Error";
import { APIStatus } from "../../app/APIStatus";

const CustomDashboards = React.lazy(() =>
  import("../../pages/custom-dashboards/CustomDashboards")
);
const Dashboard = React.lazy(() => import("../../pages/dashboard/Dashboard"));
const RemoteEyeDashboard = React.lazy(() =>
  import("../../pages/remoteEyeDashboard/RemoteEyeDashboard")
);
const RemoteEyeDiagnostics = React.lazy(() =>
  import("../../pages/remoteEyeDiagnostics")
);
const Alarms = React.lazy(() => import("../../pages/alarms/Alarms"));
const Triggers = React.lazy(() => import("../../pages/triggers/Triggers"));
const AssetsDashboard = React.lazy(() =>
  import("../../pages/assetsDashboard/AssetsDashboard")
);
const AssetsManagement = React.lazy(() =>
  import("../../pages/assetsManagement/AssetsManagement")
);
const AddAsset = React.lazy(() =>
  import("../../pages/assetsManagement/AddAsset")
);
const EditAsset = React.lazy(() =>
  import("../../pages/assetsManagement/EditAsset")
);
const ScheduleReport = React.lazy(() =>
  import("../../pages/scheduleReport/ScheduleReport")
);
const ProcessedData = React.lazy(() =>
  import("../../pages/processedData/ProcessedData")
);
const DeviceManagement = React.lazy(() =>
  import("../../pages/device-management/DeviceManagement")
);
const AccountSettings = React.lazy(() =>
  import("../../pages/accountSettings/AccountSettings")
);
const MeteringDashboard = React.lazy(() =>
  import("../../pages/meteringDashboard/MeteringDashboard")
);
const EDMDashboard = React.lazy(() =>
  import("../../pages/edmDashboard/EDMDashboard")
);
const UserManagement = React.lazy(() =>
  import("../../pages/userManagement/UserManagement")
);
const AddDevice = React.lazy(() =>
  import("../../pages/device-management/AddDevice")
);
const EditDevice = React.lazy(() =>
  import("../../pages/device-management/EditDevice")
);
const DeviceSummary = React.lazy(() =>
  import("../../pages/deviceSummary/DeviceSummary")
);
const ChangePassword = React.lazy(() =>
  import("../../pages/changePassword/ChangePassword")
);

const GisPlayground = React.lazy(() =>
  import("../../pages/playground/Playground")
);

const GisOfflineAnimation = React.lazy(() =>
  import("../../pages/playground/GisOfflineAnimation")
);

const GisConsumption = React.lazy(() =>
  import("../../pages/playground/GisConsumption")
);


const PrintPage = React.lazy(() => import("../../pages/PrintPage"));

function Layout(props) {
  var classes = useStyles();
  var layoutState = useLayoutState();
  const dispatch = useDispatch();
  usePageTracking();

  const { isLoading: dashboardConfigAPIStatus, dashboardConfig } = useSelector(
    (state) => state.configurationSlice
  );

  const [isRemoteeyeUser, setIsRemoteeyeUser] = useState(false);

  useEffect(() => {
    setIsRemoteeyeUser(
      dashboardConfig?.meta_data?.custom_config?.is_remoteeye_user?.value ||
        false
    );
  }, [dashboardConfig]);

  const getDashboardTypeToShow = () => {
    if (dashboardConfigAPIStatus !== APIStatus.loading && dashboardConfig) {
      const isRemoteeyeUser =
        dashboardConfig?.meta_data?.custom_config?.is_remoteeye_user?.value ||
        false;
      return isRemoteeyeUser ? <RemoteEyeDashboard /> : <Dashboard />;
    }

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "5%",
        }}
      >
        <CircularProgress size={24} />
      </div>
    );
  };

  useEffect(() => {
    const organization = localStorage.getItem("organization");
    if (organization) {
      let devicesHealthApi = `/${organization}/devices-health/`;
      dispatch(getDevicesHealth({ url: devicesHealthApi }));
    }
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <>
        <Header history={props.history} />
        <Sidebar />
        <div
          className={classnames(classes.content, {
            [classes.contentShift]: layoutState.isSidebarOpened,
          })}
        >
          <Box minHeight={64} />
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "5%",
                }}
              >
                <CircularProgress size={24} />
              </div>
            }
          >
            <Switch>
              <Route path="/app/dashboard">{getDashboardTypeToShow()}</Route>
              {isRemoteeyeUser && (
                <Route path="/app/diagnostics">
                  <RemoteEyeDiagnostics />
                </Route>
              )}
              <Route path="/app/alarms">
                <Alarms />
              </Route>
              <Route path="/app/triggers">
                <Triggers />
              </Route>
              <Route path="/app/assetsDashboard">
                <AssetsDashboard />
              </Route>
              <Route path="/app/assetsManagement">
                <AssetsManagement />
              </Route>
              <Route path="/app/addAsset">
                <AddAsset />
              </Route>
              <Route path="/app/editAsset/:assetId">
                <EditAsset />
              </Route>
              <Route path="/app/processedData">
                <ProcessedData />
              </Route>
              <Route path="/app/deviceManagement/manageDevices">
                <DeviceManagement />
              </Route>
              <Route path="/app/deviceManagement/addDevice">
                <AddDevice />
              </Route>
              <Route path="/app/deviceManagement/editDevice/:deviceId">
                <EditDevice />
              </Route>
              <Route path="/app/deviceSummary/:deviceId">
                <DeviceSummary />
              </Route>
              <Route path="/app/changePassword">
                <ChangePassword />
              </Route>
              <Route exact path="/app/accountSettings">
                <Redirect to="/app/accountSettings/0" />
              </Route>
              <Route path="/app/accountSettings/:activeTab">
                <AccountSettings />
              </Route>
              <Route path="/app/meteringDashboard">
                <MeteringDashboard />
              </Route>
              <Route path="/app/edmDashboard">
                <EDMDashboard />
              </Route>
              <Route path="/app/userManagement">
                <UserManagement />
              </Route>
              <Route path="/app/CustomDashboards/:selectedDashboardKey">
                <CustomDashboards />
              </Route>
              <Route path="/app/scheduleReports">
                <ScheduleReport />
              </Route>
              <Route path="/app/deviceAlarmDashboard/:device_id?alarm_id=:alarmId">
                <PrintPage />
              </Route>
              <Route path="/app/GisPlayground">
                <GisPlayground />
              </Route>
              <Route path="/app/GisOffline">
                <GisOfflineAnimation />
              </Route>
              <Route path="/app/GisConsumption">
                <GisConsumption />
              </Route>
              <Route path="*">
                <Error />
              </Route>
            </Switch>
          </Suspense>
        </div>
      </>
    </div>
  );
}

export default withRouter(Layout);
